<template>
  <div class="flex flex-wrap">
    <div class="w-full md:pl-10 md:w-3/4 md:pr-20">
      <div class="mb-5" v-if="allowThirdParty">
        <h1 class="mb-4 font-sans-bold">Ticketing</h1>
        <p class="mb-5">
          Reimagine has agreed to make an exception for you to use a third-party ticketing vendor for your event. Please provide a fully qualified URL to link out to your ticketing page (must include http:// or https:// – e.g., https://letsreimagine.org).
        </p>
        <label for="thirdPartyUrl" ref="thirdPartyUrl">
          Ticket URL
          <span class="text-red">*</span>
        </label>

        <div class="flex">
          <div
            class="inline-flex flex-initial px-4 border-t border-b border-l border-black bg-gray-hr"
          >
            <span class="self-center">http(s)://</span>
          </div>

          <div class="flex-grow">
            <input
              id="thirdPartyUrl"
              type="text"
              name="thirdPartyUrl"
              v-validate="{ required: true, url: { require_tld: true }}"
              v-model="form.thirdPartyUrl"
              @keyup="filterUrl(form.thirdPartyUrl)"
            />
          </div>

          <a
            class="inline-flex px-2 text-white no-underline bg-black md:px-8"
            :href="`https://${form.thirdPartyUrl}`"
            target="_blank"
          >
            <span class="self-center uppercase">Test Link</span>
          </a>
        </div>
        <span v-show="errors.has('thirdPartyUrl')" class="text-lg error text-error-red">
          {{ errors.first('thirdPartyUrl') }}
        </span>
      </div>
      <div v-else>
        <h1 class="mb-4">Ticketing</h1>
        <p class="mb-5">
          We partner with SquadUP to power our ticketing system. Please review SquadUP's <a href="https://www.squadup.com/tos" target="_blank">Terms of Service</a> and <a href="https://www.squadup.com/faq" target="_blank">FAQ</a>.
        </p>

        <div class="mb-12">
          <label for="price" ref="price">
            General Admission Price (USD)
            <span class="text-red">*</span>
            <div class="block text-sm font-sans-book border-b border-gray-medium pb-5 mb-5">
              <p class="mb-4">
                If you are hosting a paid event, please enter the General Admission (GA) ticket price (whole number, no dollar sign, e.g. 10). If you are hosting a free event, please enter the number 0 in this field.
              </p>
              <p>
                You can also rename the ticketing options by clicking on the text (e.g. "Option 1") and typing in a new label name (e.g. "Free").
              </p>
            </div>
          </label>
          <div name="name" class="flex items-center border-b border-gray-medium pb-5 mb-5" v-for="(ticket, index) in form.tickets" :key="index">
            <div class="font-bold text-lg w-48 lg:w-68 pr-5">
              <input
                id="name"
                type="text"
                name="name"
                class="flex-1 short-input"
                v-validate="'required'"
                v-model="ticket.name"
              />
            </div>
            <input
              id="price"
              type="text"
              name="price"
              class="flex-1 short-input"
              v-validate="'required|numeric'"
              v-model="ticket.price"
            />
            <button class="pl-3" v-if="form.tickets.length > 1" @click.stop.prevent="removeTicketOption(index)"><svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="trash-alt" class="svg-inline--fa fa-trash-alt fa-w-14 w-3" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M32 464a48 48 0 0 0 48 48h288a48 48 0 0 0 48-48V128H32zm272-256a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zm-96 0a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zm-96 0a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zM432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16z"></path></svg></button>
          </div>
          <span v-show="errors.has('name')" class="text-lg error text-error-red">
            {{ errors.first('name') }}
          </span>
          <span v-show="errors.has('price')" class="text-lg error text-error-red">
            {{ errors.first('price') }}
          </span>
          <button v-if="form.tickets.length < 4" class="button mt-3" @click.stop="addTicketOptions">
            {{ ticketOptionsButton }}
          </button>
        </div>

        <div class="mb-12">
          <label for="amount" ref="amount">
            Event Capacity
            <span class="text-red">*</span>
            <span class="block text-sm font-sans-book">
              This is the maximum number of tickets to be sold. If your event does not have a maximum capacity, please enter 9999 in this field.
            </span>
          </label>
          <input
            id="amount"
            type="text"
            name="amount"
            v-validate="'required|numeric'"
            v-model="form.maxTickets"
          />
          <span v-show="errors.has('amount')" class="text-lg error text-error-red">
            {{ errors.first('amount') }}
          </span>
        </div>

        <template v-if="hasPaidTicketOption">
          <div class="mb-12">
            <label>How would you like to receive payment from ticket sales?</label>
            <RadioGroup
              v-model="form.paymentMethod"
              name="paymentMethod"
              :options="paymentMethods"
              v-validate="'required'"
            />
            <span v-show="errors.has('paymentMethod')" class="text-lg error text-error-red">
              {{errors.first('paymentMethod') }}
            </span>
          </div>
          <div class="mb-12" v-if="form.paymentMethod">
            <label for="paymentDetails">{{ paymentDetailsLabel }}</label>
            <input v-model="form.paymentDetails" v-validate="'required'" id="paymentDetails" name="paymentDetails" type="text" v-if="form.paymentMethod !== 'check'" />
            <textarea v-model="form.paymentDetails" v-validate="'required'" id="paymentDetails" name="paymentDetails" v-else />
            <span v-show="errors.has('paymentDetails')" class="text-lg error text-error-red">
              {{ errors.first('paymentDetails') }}
            </span>
          </div>
        </template>
      </div>

      <div class="flex md:flex-no-wrap flex-wrap">
        <button
          class="button"
          @click.stop.prevent="save"
          :class="{ 'opacity-50 cursor-not-allowed': saving }"
          :disabled="saving"
        >
          <template v-if="saving">Processing...</template>
          <template v-else>Save</template>
        </button>
        <div class="mr-0 md:mr-4 mb-4 md:mb-4 w-full md:w-auto" />
        <button
          class="button"
          @click.stop.prevent="saveNext"
          :class="{ 'opacity-50 cursor-not-allowed': saving }"
          :disabled="saving"
        >Save & Next</button>
      </div>
    </div>
    <div class="w-full pt-3 mt-10 border-black md:w-1/4 md:mt-2" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import eventSave from "../../../mixins/event-save";
import messaging from "../../../mixins/messaging";
import urlFilter from "../../../mixins/url-filter";
import validation from "../../../mixins/validation";
import RadioGroup from "../../global/RadioGroup.vue";

export default {
  name: "EventCreateTickets",
  components: {RadioGroup},
  title: "Create Event Tickets",
  mixins: [eventSave, messaging, urlFilter, validation],
  data: () => ({
    defaultPrices: [0, 10, 25, 50],
    form: {
      maxTickets: null,
      thirdPartyUrl: null,
      tickets: [],
      paymentMethod: null,
      paymentDetails: null,
    },
    maxTickets: 4,
    next: "events-submit",
    paymentMethods: [
      {title: 'Venmo', value: 'venmo', details: 'What is your username?'},
      {title: 'Paypal', value: 'paypal', details: 'What is your username/email?'},
      {title: 'Mailed Check', value: 'check', details: 'Please enter your mailing address.'},
    ]
  }),
  watch: {
    event(event) {
      if (!_.isEmpty(event)) {
        if (event.tickets.length) {
          this.$set(this.form, "tickets", event.tickets);
        } else {
          this.$set(this.form, "tickets", [{ name: 'Option 1', price: 0 }]);
        }

        this.form.maxTickets = event.maxTickets;

        this.form.thirdPartyUrl = this.filterUrl(event.thirdPartyUrl);
        this.form.paymentMethod = event.paymentMethod;
        this.form.paymentDetails = event.paymentDetails;

        this.$nextTick(function () {
          this.$store.dispatch("setUnsavedData", false);
        });
      }
    },
  },
  computed: {
    ...mapGetters({
      event: "event",
      isLoading: "isLoading",
    }),
    allowThirdParty() {
      return this.event.allowThirdParty;
    },
    ticketOptionsButton() {
      if (this.form.tickets.length === 1) {
        return 'Create Sliding Scale?';
      }

      return 'Add Option';
    },
    hasPaidTicketOption() {
      return this.form.tickets.find(ticket => ticket.price > 0);
    },
    paymentDetailsLabel() {
      return this.form.paymentMethod ? this.paymentMethods.find(paymentMethod => paymentMethod.value === this.form.paymentMethod).details : null;
    }
  },
  methods: {
    addTicketOptions() {
      this.form.tickets.push({
        price: this.defaultPrices[this.form.tickets.length],
        name: 'Option 2'
      });

      if (this.form.tickets.length === 2) {
        for (let i = 2; i < this.maxTickets; i++) {
          this.form.tickets.push({
            price: this.defaultPrices[i],
            name: 'Option ' + (i + 1)
          });
        }
      }
    },

    removeTicketOption(index) {
      this.form.tickets.splice(index, 1);
    },

    save() {
      this.saving = true;
      this.sortTickets();
      this.$store.dispatch("setEventIsLoading", true);

      return this.$validator.validateAll().then((result) => {
        if (! result) {
          this.saving = false;
          this.gotoFirstValidationError();
          return;
        }

        this.$toasted.show("Event currently saving...", { duration: null});

        let route = this.$laroute.route("api.events.update.tickets", {
          event: this.event.id,
        });

        if (!this.hasPaidTicketOption) {
          this.form.paymentDetails = null;
          this.form.paymentMethod = null;
        }

        return axios
          .put(route, this.form)
          .then((response) => {

            this.$toasted.clear();
            this.showUpdatedToast();

            let eventResponse = response.data.data;

            this.$store.dispatch("setActiveEvent", response.data);
            this.$store.dispatch("setValidation", eventResponse);
            this.$store.dispatch("setUnsavedData", false);
            this.$store.dispatch("setEventHasErrors", false);
            this.$store.dispatch("setEventIsLoading", false);

            this.saving = false;
          })
          .catch((error) => {
            this.displayError(error);
          });
      });
    },
    sortTickets() {
      this.$set(this.form, "tickets", _.sortBy(this.form.tickets, [ticket => Number(ticket.price)]));
    },
  },
  mounted() {
    this.$store.dispatch("getEvent", { id: this.$route.params.id });
    document.getElementById("dropdown").checked = false;
  },
};
</script>
